import React, { lazy, useContext, useMemo } from "react";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { AddTranslationsContext } from "src/hooks/IntlProvider";
import { addOnStopStorage } from "src/store/storageSaga";
import { store } from "src/store/store";
import ErrorBoundary from "./ErrorBoundary";

const useDynamicPage = ({
  moduleName,
  selectState,

  importPage,
  importStyle,
  importTranslations,

  importReducer,
  importGetInitState,
  importStartSyncStorage,
  importSaga,

  LoadErrorComponent,
  CrashErrorComponent,
  moduleRetained = false
}) => {
  const addTranslations = useContext(AddTranslationsContext);

  const LazyComponent = useMemo(() => lazy(async () => {
    const translations = await importTranslations?.();
    if (translations) addTranslations(translations);
    importStyle?.();

    const [
      ModulePage,
      moduleReducer,
      moduleSaga,
      getInitState,
      startSyncStorage
    ] = await Promise.all(
      [
        importPage,
        importReducer,
        importSaga,
        importGetInitState,
        importStartSyncStorage
      ].map(f => f?.())
    );

    if (startSyncStorage) {
      const waitForStorageReady = getInitState !== undefined;
      const stopSyncStorage = startSyncStorage(
        store,
        selectState,
        waitForStorageReady
      );
      addOnStopStorage(stopSyncStorage);
    }

    let componentModule = {
      id: moduleName,
      reducerMap: {},
      sagas: [],
      initialActions: [],
      retained: moduleRetained
    };

    if (moduleSaga) componentModule.sagas.push(moduleSaga);
    if (moduleReducer) {
      const INIT_PAGE = "INIT_PAGE";
      const addStateInit = reducer => (state, action) => {
        let stateToPass = state;
        const shouldInitState = getInitState &&
          action.type === INIT_PAGE &&
          action.moduleName === moduleName;
        if (shouldInitState) stateToPass = {
          ...getInitState(),
          localStorageReady: true,
        };
        return reducer(stateToPass, action);
      };

      componentModule.reducerMap[moduleName] = addStateInit(moduleReducer);
      componentModule.initialActions.push({ type: INIT_PAGE, moduleName });
    }

    const ResultComponent = props => (
      <DynamicModuleLoader modules={[componentModule]}>
        <ModulePage {...props} />
      </DynamicModuleLoader>
    );

    return { default: ResultComponent };
  }), [
    moduleName,
    selectState,
    importPage,
    importStyle,
    addTranslations,
    importTranslations,

    importReducer,
    importSaga,
    importGetInitState,
    importStartSyncStorage,
    moduleRetained
  ]);

  return useMemo(() => props => (
    <ErrorBoundary
      LoadErrorComponent={LoadErrorComponent}
      CrashErrorComponent={CrashErrorComponent}
    >
      <LazyComponent {...props} />
    </ErrorBoundary>
  ), [LoadErrorComponent, CrashErrorComponent]);
};

export default useDynamicPage;
